<template>
  <div
    class="relative bg-gradient-to-br from-white via-gray-50 to-logo-color/5 py-20 md:py-32 overflow-hidden"
  >
    <!-- Background Decorative Shapes -->
    <div class="absolute inset-0 pointer-events-none">
      <div
        class="absolute top-20 left-10 w-72 h-72 bg-logo-color/5 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute bottom-20 right-10 w-96 h-96 bg-logo-color/5 rounded-full blur-3xl"
      ></div>
      <!-- Geometric shapes -->
      <div
        class="absolute top-40 right-20 w-20 h-20 border-4 border-logo-color/10 rounded-full hidden lg:block"
      ></div>
      <div
        class="absolute bottom-40 left-20 w-16 h-16 bg-logo-color/10 rotate-45 hidden lg:block"
      ></div>
      <svg
        class="absolute top-1/4 left-1/4 w-32 h-32 text-logo-color/5 hidden lg:block"
        viewBox="0 0 100 100"
      >
        <polygon points="50,5 95,95 5,95" fill="currentColor" />
      </svg>
    </div>

    <div class="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <!-- Main content section -->
      <div class="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
        <!-- Left side: Image Gallery -->
        <div
          class="w-full lg:w-1/2 relative"
          data-aos="fade-right"
          data-aos-duration="1000"
        >
          <div class="relative">
            <!-- Main Image -->
            <div class="relative max-w-lg mx-auto lg:mx-0">
              <div
                class="relative rounded-3xl overflow-hidden shadow-[0_25px_60px_-15px_rgba(0,0,0,0.25)]"
              >
                <img
                  class="w-full h-[400px] object-cover"
                  :src="require('@/assets/hands.jpg')"
                  alt="Youth Empowerment"
                />
                <div
                  class="absolute inset-0 bg-gradient-to-t from-logo-color/60 via-transparent to-transparent"
                ></div>
                <!-- Overlay Content -->
                <div class="absolute bottom-0 left-0 right-0 p-6">
                  <p class="text-white/80 text-sm font-medium">Since 2019</p>
                  <h3 class="text-xl font-bold text-white">
                    Transforming Lives
                  </h3>
                </div>
              </div>

              <!-- Decorative frame -->
              <div
                class="absolute -bottom-4 -right-4 w-full h-full bg-logo-color/10 rounded-3xl -z-10 hidden lg:block"
              ></div>
            </div>

            <!-- Floating Small Images -->
            <div
              class="absolute -bottom-10 right-16 w-32 h-32 rounded-2xl overflow-hidden shadow-xl border-4 border-white z-20 hidden lg:block"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <img
                :src="require('@/assets/happy.jpg')"
                alt="STEM Training"
                class="w-full h-full object-cover"
              />
            </div>

            <div
              class="absolute top-8 -right-6 w-28 h-28 rounded-2xl overflow-hidden shadow-xl border-4 border-white z-20 hidden lg:block"
              data-aos="fade-down"
              data-aos-delay="300"
            >
              <img
                :src="require('@/assets/smiles.jpg')"
                alt="Empowerment"
                class="w-full h-full object-cover"
              />
            </div>

            <!-- Floating Badge -->
            <div
              class="absolute top-4 left-4 bg-white px-4 py-2 rounded-full shadow-lg z-30 flex items-center gap-2"
              data-aos="zoom-in"
              data-aos-delay="400"
            >
              <span
                class="w-2 h-2 bg-green-500 rounded-full animate-pulse"
              ></span>
              <span class="text-sm font-semibold text-gray-700"
                >Making Impact</span
              >
            </div>
          </div>
        </div>

        <!-- Right side: Content -->
        <div
          class="w-full lg:w-1/2 relative"
          data-aos="fade-left"
          data-aos-duration="1000"
        >
          <!-- Badge -->
          <div class="flex items-center gap-3 mb-6">
            <span
              class="inline-flex items-center gap-2 px-4 py-2 bg-logo-color/10 rounded-full"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 text-logo-color"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span
                class="text-sm font-semibold text-logo-color uppercase tracking-wider"
                >Our Expertise</span
              >
            </span>
          </div>

          <!-- Main heading -->
          <h2
            class="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4"
          >
            What We Do
            <span class="text-logo-color relative">
              Best
              <svg
                class="absolute -bottom-2 left-0 w-full"
                height="8"
                viewBox="0 0 200 8"
                fill="none"
              >
                <path
                  d="M1 5.5C47.6667 2.16667 141.4 -2.1 199 5.5"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  class="text-logo-color/30"
                />
              </svg>
            </span>
          </h2>

          <p class="text-gray-600 text-lg mb-8 leading-relaxed">
            At Youth Ally, we inspire and equip young people with the ethics,
            skills and transformative mindset to be self-reliant and agents of
            change.
          </p>

          <!-- Expertise cards -->
          <div class="space-y-4 mb-8">
            <div
              v-for="(expertise, index) in expertiseAreas"
              :key="index"
              class="group bg-white rounded-2xl p-5 transition-all duration-300 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_15px_40px_-10px_rgba(0,0,0,0.2)] hover:-translate-y-1 border border-gray-100"
              :data-aos="'fade-up'"
              :data-aos-delay="index * 100"
            >
              <div class="flex items-center gap-4">
                <div
                  class="w-14 h-14 bg-logo-color/10 rounded-2xl flex items-center justify-center group-hover:bg-logo-color transition-all duration-300 flex-shrink-0"
                >
                  <i
                    :class="expertise.icon"
                    class="text-2xl text-logo-color group-hover:text-white transition-colors"
                  ></i>
                </div>
                <div>
                  <h3
                    class="text-lg font-bold text-gray-900 group-hover:text-logo-color transition-colors"
                  >
                    {{ expertise.title }}
                  </h3>
                  <p class="text-gray-500 text-sm">
                    {{ expertise.description }}
                  </p>
                </div>
                <div
                  class="ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <i class="fas fa-chevron-right text-logo-color"></i>
                </div>
              </div>
            </div>
          </div>

          <!-- Feature Pills -->
          <div class="flex flex-wrap gap-3">
            <div
              class="flex items-center gap-2 px-4 py-2 bg-logo-color/10 rounded-full"
            >
              <span class="w-2 h-2 bg-logo-color rounded-full"></span>
              <span class="text-sm font-medium text-logo-color"
                >Hands-on Training</span
              >
            </div>
            <div
              class="flex items-center gap-2 px-4 py-2 bg-logo-color/10 rounded-full"
            >
              <span class="w-2 h-2 bg-logo-color rounded-full"></span>
              <span class="text-sm font-medium text-logo-color"
                >Mentorship</span
              >
            </div>
            <div
              class="flex items-center gap-2 px-4 py-2 bg-logo-color/10 rounded-full"
            >
              <span class="w-2 h-2 bg-logo-color rounded-full"></span>
              <span class="text-sm font-medium text-logo-color"
                >Community Impact</span
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ExpertiseSection",
  data() {
    return {
      expertiseAreas: [
        {
          title: "Ethical Leadership",
          description:
            "Building leaders with integrity, ethics and transformative mindset",
          icon: "fas fa-shield-alt",
        },
        {
          title: "Workforce Development",
          description:
            "Equipping youth with practical skills for workplace success",
          icon: "fas fa-briefcase",
        },
        {
          title: "Entrepreneurship",
          description: "Developing innovative solutions to community problems",
          icon: "fas fa-lightbulb",
        },
      ],
    };
  },
};
</script>

<style scoped>
/* Modern design with shapes */
</style>
